import { Box, ListItemButton, ListItemIcon, ListItemText, useTheme } from "@mui/material";
import Icons from "components/common/Icons";
import { TRoute, useRouteStates } from "models/RouteModel";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DragHandle from "@mui/icons-material/DragHandle";
import { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import {
  useLeftNavIsEditMode,
  useLeftNavItems,
  useLeftNavOpenFolders,
  useSetMenuDeleteFormOpen,
  useSetMenuDeleteFormRoute,
  useSetMenuEditFormOpen,
  useSetMenuEditFormRoute,
} from "globalStates/leftNavState";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import { useSharedPages } from "models/SharedPageModel";

const CloudIcons = {
  download: <CloudDownloadOutlined color="success" sx={{ verticalAlign: "middle", height: 15 }} />,
  upload: <CloudUploadOutlined color="primary" sx={{ verticalAlign: "middle", height: 15 }} />,
  upload_disabled: <CloudUploadOutlined color="disabled" sx={{ verticalAlign: "middle", height: 15 }} />,
};

type TDndLeftNavItem = {
  item: TRoute;
  isOverlay?: boolean;
  dragHandleProps?: {
    ref: (element: HTMLElement | null) => void;
    attributes: DraggableAttributes;
    listeners: DraggableSyntheticListeners;
  };
};
const DndLeftNavItem = ({ item, isOverlay, dragHandleProps }: TDndLeftNavItem) => {
  const theme = useTheme();
  const buttonSx = {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: theme.palette.action.active,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  };

  const navigate = useNavigate();
  const { setCurrentRoute, currentRoute } = useRouteStates();
  const { isSharedPageBaseRoute, isSharedPageUseRoute, isDisabledSharedPageBaseRoute } = useSharedPages();

  const depth = item.depth ? item.depth : 0;

  const { setClose, isOpen: isOpenParent } = useLeftNavOpenFolders();
  const { setItemsOnCollapse } = useLeftNavItems();
  const isOpen = isOpenParent(item.id);
  const setEditFormOpen = useSetMenuEditFormOpen();
  const setEditFormRoute = useSetMenuEditFormRoute();
  const setDeleteFormOpen = useSetMenuDeleteFormOpen();
  const setDeleteFormRoute = useSetMenuDeleteFormRoute();

  const isEditMode = useLeftNavIsEditMode();

  const onClickItem = useCallback(
    (item: TRoute) => {
      if (item.type === "folder") {
        isOpen ? setClose(item.id) : setItemsOnCollapse(item.id);
      } else {
        item.url && navigate(item.url);
        setCurrentRoute(item);
      }
    },
    [isOpen, navigate, setClose, setCurrentRoute, setItemsOnCollapse],
  );

  const handleOnEdit = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      setEditFormOpen(true);
      setEditFormRoute(item);
    },
    [item, setEditFormOpen, setEditFormRoute],
  );

  const handleOnDelete = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      setDeleteFormOpen(true);
      setDeleteFormRoute(item);
    },
    [item, setDeleteFormOpen, setDeleteFormRoute],
  );

  const getIconName = useCallback(() => {
    let name = "";
    if (item.type === "folder") name = isOpen ? "FolderOpen" : "Folder";
    else if (item.icon) name = item.icon;
    return name;
  }, [isOpen, item.icon, item.type]);

  return (
    <ListItemButton
      dense
      id={item.id}
      sx={{ pl: (depth + 1) * 2 }}
      onClick={() => onClickItem(item)}
      selected={currentRoute && currentRoute.id === item.id}
    >
      <ListItemIcon>
        <Icons iconName={getIconName()} />
      </ListItemIcon>
      <ListItemText>
        {item.title}
        {isSharedPageUseRoute(item) && CloudIcons.download}
        {isSharedPageBaseRoute(item) &&
          (isDisabledSharedPageBaseRoute(item) ? CloudIcons.upload_disabled : CloudIcons.upload)}
      </ListItemText>
      {item.type === "folder" && (isOpen ? <Icons iconName="ExpandLess" /> : <Icons iconName="ExpandMore" />)}
      {isEditMode && (
        <>
          {!isOverlay && (
            <>
              <Box sx={buttonSx} onClick={handleOnEdit}>
                <Icons iconName="EditOutlined" />
              </Box>
              <Box sx={buttonSx} onClick={handleOnDelete}>
                <Icons iconName="DeleteForever" />
              </Box>
            </>
          )}
          <Box
            ref={dragHandleProps?.ref}
            {...dragHandleProps?.attributes}
            {...dragHandleProps?.listeners}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: dragHandleProps ? "grab" : "grabbing",
              color: theme.palette.action.active,
              "&:hover": {
                color: theme.palette.text.primary,
              },
            }}
          >
            <DragHandle />
          </Box>
        </>
      )}
    </ListItemButton>
  );
};

export default DndLeftNavItem;
